import TokenStore from "../classes/TokenStore.js";
import BluffsGroups from "../classes/BluffsGroups.js";
import Dialog from "../classes/Dialog.js";
import InfoToken from "../classes/InfoToken.js";
import Names from "../classes/Names.js";
import {
    lookup,
    lookupOne,
    lookupOneCached,
    announceInput
} from "../utils/elements.js";
import {
    VERSION
} from "../constants/version.js";
import {
    gameObserver,
    infoTokenObserver,
    mainStore,
    tokenObserver
} from "../constants/observers.js";
import {
    compareVersions
} from "../utils/numbers.js";



const padElement = lookupOneCached(".js--pad");
const pad = padElement.pad;
const names = Names.create();

gameObserver.on("characters-selected", ({ detail }) => {

    mainStore.setCharacters(
        detail.meta,
        detail.characters.map((character) => (
            character.isCustom()
            ? character.getAllData()
            : character.getId()
        )),
        detail.game
    );
    mainStore.setData("importedRoles", null);
    mainStore.removeStaleInputs();

});

gameObserver.on("pad-height-change", ({ detail }) => {
    mainStore.setHeight(detail.height);
});

tokenObserver.on("character-add", ({ detail }) => {
    mainStore.addToken(detail.character);
});

tokenObserver.on("character-remove", ({ detail }) => {
    mainStore.removeToken(detail.character);
});

tokenObserver.on("reminder-add", ({ detail }) => {
    mainStore.addToken(detail.reminder);
});

tokenObserver.on("reminder-remove", ({ detail }) => {
    mainStore.removeToken(detail.reminder);
});

const getTokenByElement = (element) => (
    pad.getCharacterByToken(element)
    || pad.getReminderByToken(element)
);

tokenObserver.on("move", ({ detail }) => {

    const {
        element,
        left,
        top,
        zIndex
    } = detail;
    const token = getTokenByElement(element);

    if (token) {
        mainStore.moveToken(token, left, top, zIndex);
    }

});

tokenObserver.on("zindex", ({ detail }) => {

    const {
        element,
        zIndex
    } = detail;
    const token = getTokenByElement(element);

    if (token) {
        mainStore.alignToken(token, zIndex);
    }

});

tokenObserver.on("conditions-change", ({ detail }) => {
    mainStore.setConditions(detail.character, detail.conditions);
});

tokenObserver.on("shroud-toggle", ({ detail }) => {
    mainStore.toggleDead(pad.getCharacterByToken(detail.token), detail.isDead);
});

tokenObserver.on("rotate-toggle", ({ detail }) => {
    mainStore.rotate(pad.getCharacterByToken(detail.token), detail.isUpsideDown);
});

tokenObserver.on("set-player-name", ({ detail }) => {

    mainStore.setPlayerName(pad.getCharacterByToken(detail.token), detail.name);
    names.add(detail.name);

});

tokenObserver.on("ghost-vote-toggle", ({ detail }) => {

    mainStore.setGhostVote(
        pad.getCharacterByToken(detail.token),
        detail.hasGhostVote
    );

});

tokenObserver.on("bluff", ({ detail }) => {
    mainStore.setBluffs(detail.data);
});

infoTokenObserver.on("info-token-added", ({ detail }) => {
    mainStore.saveInfoToken(detail.token, detail.index);
});

infoTokenObserver.on("info-token-updated", ({ detail }) => {
    mainStore.updateInfoToken(detail.token);
});

infoTokenObserver.on("info-token-deleted", ({ detail }) => {
    mainStore.removeInfoToken(detail.token);
});

const savedVersion = mainStore.getVersion();

if (!savedVersion || compareVersions(savedVersion, VERSION) === -1) {

    if (
        savedVersion
        && window.confirm(I18N.versionChange)
    ) {
        Dialog.create(lookupOneCached("#clear-cache")).show();
    }

    mainStore.setVersion(VERSION);

}

const {
    body
} = document;

body.addEventListener("input", ({ target }) => {

    const input = target.closest("input,select,textarea");

    if (!input.hasAttribute("data-no-store")) {
        mainStore.saveInput(input);
    }

});

body.addEventListener("toggle", ({ target }) => {
    mainStore.saveDetails(target.closest("details"));
}, {
    capture: true
});

const storeData = mainStore.read();

TokenStore.ready((tokenStore) => {

    // Re-select the characters.

    const info = storeData.characters;

    if (info && info.characters && info.characters.length) {

        gameObserver.trigger("characters-selected", {
            meta: info.meta,
            characters: info.characters
                .map((item) => (
                    tokenStore.getCharacter(
                        typeof item === "string"
                        ? item
                        : item.id
                    )
                    || tokenStore.createCustomCharacter(item)
                ))
                .filter(Boolean),
            game: info.game
        });

    }

    // Re-place the tokens.

    let finalZIndex = 0;

    storeData.tokens.forEach(({
        id,
        left,
        top,
        zIndex,
        isDead,
        isUpsideDown,
        playerName,
        ghostVote,
        conditions = {},
        reminderData
    }) => {

        const importedReminder = TokenStore.isReminderId(id) && reminderData?.imported === true;
        if (!importedReminder && !tokenStore.characterExists(id)) {
            return console.warn(`Unable to find character/reminder with ID '${id}'`);
        }

        const isCharacter = TokenStore.isCharacterId(id);
        const info = (
            isCharacter
            ? pad.addCharacter(tokenStore.getCharacterClone(id))
            : pad.addReminder(importedReminder ? tokenStore.createImportedReminder(reminderData) : tokenStore.getReminderClone(id))
        );
        const {
            token,
            character
        } = info;

        pad.moveToken(token, left, top, zIndex);

        if (isCharacter) {

            pad.toggleDead(character, Boolean(isDead));
            pad.rotate(character, Boolean(isUpsideDown));
            pad.setPlayerName(character, playerName);
            pad.setGhostVote(character, Boolean(ghostVote));
            pad.setConditions(character, conditions);

        } else {
            info.reminder.conditions = conditions;
            mainStore.setConditions(info.reminder, conditions);
        }

        if (zIndex > finalZIndex) {
            finalZIndex = zIndex;
        }

    });

    pad.setZIndex(finalZIndex);

    if (Array.isArray(storeData.importedRoles)) {
        mainStore.setData("importedRoles", storeData.importedRoles);
        gameObserver.trigger("imported-roles", {
            characters: storeData.importedRoles.map((id) => tokenStore.getCharacter(id)).filter(Boolean)
        });
    }

    // Re-set the bluffs.
    // Convert the previous bluffs format into the new one.

    let {
        bluffs
    } = storeData;

    if (!Object.keys(bluffs).length) {
        bluffs = BluffsGroups.getEmptyData();
    }

    if (typeof bluffs["#bluff-1"] === "string") {

        const empty = BluffsGroups.getEmptyData();
        empty.groups[0].set = Object.values(bluffs);
        bluffs = empty;

    }

    BluffsGroups.get().ready(bluffs);

    // Re-populate the inputs.

    Object.entries(storeData.inputs).forEach(([selector, value]) => {

        const inputs = lookup(selector);

        const type = inputs[0]?.type;
        const isRadio = type === "radio";
        const input = (
            isRadio
            ? inputs.find((input) => input.value === value)
            : inputs[0]
        );

        if (!input) {
            return;
        }

        if (isRadio) {
            input.checked = true;
        } else if (type === "checkbox") {
            input.checked = value;
        } else {
            input.value = value;
        }

        announceInput(input);

    });

    gameObserver.trigger("inputs-repopulated");

    // Re-open or re-close the details.

    Object.entries(storeData.details).forEach(([selector, isOpen]) => {

        const details = lookupOne(selector);

        if (!details) {
            return;
        }

        const isOpenNow = details.hasAttribute("open");
        details.open = isOpen;

        if (isOpenNow !== isOpen) {

            details.dispatchEvent(new Event("toggle", {
                bubbles: false
            }));

        }

    });

    // Re-set the height of the pad.
    // CSS `resize` works by changing the `height` style on the element itself
    // which is why we're setting it like this instead of setting a CSS custom
    // property and referring to it in the style sheet.

    if (storeData.height) {
        padElement.style.height = storeData.height;
    }

    // Re-add any custom info tokens.

    storeData.infoTokens.forEach((raw, i) => {

        const token = new InfoToken({
            raw,
            custom: true
        });
        token.draw();

        infoTokenObserver.trigger("info-token-added", {
            token,
            index: i
        });

    });

});
